import { useState, useEffect, useCallback } from 'react';
import { AnalyticsEvents, trackEvent } from '../analytics/events';
import { devQuery } from '../constants/devControls';
import {
  isAndroidDevice,
  isInStandaloneMode,
  isIosDevice,
  probeRelatedWebAppInstalled,
  shouldOfferAndroidPwaRepair,
} from './pwaPlatform';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

const SNOOZE_KEY = 'riftbound.pwa-banner-snooze';
const ANDROID_REPAIR_SNOOZE_KEY = 'riftbound.pwa-android-repair-snooze';
const SNOOZE_DAYS = 30;
const MAX_DISMISSALS = 2;
const ANDROID_REPAIR_SNOOZE_DAYS = 7;

interface SnoozeRecord {
  /** Unix ms of last dismissal. */
  at: number;
  /** How many times the user has dismissed. */
  count: number;
}

function readSnooze(key: string): SnoozeRecord | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as SnoozeRecord) : null;
  } catch {
    return null;
  }
}

function isSnoozed(key: string, days: number, maxDismissals = MAX_DISMISSALS): boolean {
  const rec = readSnooze(key);
  if (!rec) return false;
  if (rec.count >= maxDismissals) return true;
  const msSinceSnooze = Date.now() - rec.at;
  return msSinceSnooze < days * 24 * 60 * 60 * 1000;
}

function writeSnooze(key: string): number {
  const prev = readSnooze(key);
  const count = (prev?.count ?? 0) + 1;
  try {
    localStorage.setItem(key, JSON.stringify({ at: Date.now(), count }));
  } catch {
    /* localStorage unavailable */
  }
  return count;
}

export interface InstallPromptState {
  /** True when the native Android/Chrome install prompt is available. */
  canInstall: boolean;
  /** True when running as an installed PWA (standalone mode). */
  isInstalled: boolean;
  /** True on iOS Safari where we can only show manual instructions. */
  isIos: boolean;
  /** True on Android browsers (Chrome / WebView UA). */
  isAndroid: boolean;
  /**
   * True when Chrome reports the WebAPK as installed but this tab is not
   * standalone — offer recovery for the "requires Chrome" launch dialog.
   */
  needsAndroidRepair: boolean;
  /**
   * True when the banner should be suppressed — either within the 30-day snooze
   * window after the first dismissal, or permanently after two dismissals.
   */
  isSnoozed: boolean;
  /** True when the Android repair banner is snoozed. */
  isAndroidRepairSnoozed: boolean;
  /** How many times the user has dismissed (0, 1, or 2+). */
  dismissCount: number;
  /** Show the native install prompt. No-op if unavailable. */
  install: () => Promise<void>;
  /**
   * Record a dismissal. First call snoozes for 30 days; second call
   * suppresses the banner permanently.
   */
  dismiss: () => void;
  /** Snooze the Android WebAPK repair banner. */
  dismissAndroidRepair: () => void;
}

export function useInstallPrompt(): InstallPromptState {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [snoozed, setSnoozed] = useState(false);
  const [androidRepairSnoozed, setAndroidRepairSnoozed] = useState(false);
  const [dismissCount, setDismissCount] = useState(0);
  const [isIos, setIsIos] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [relatedWebAppInstalled, setRelatedWebAppInstalled] = useState(false);

  useEffect(() => {
    const standalone = isInStandaloneMode();
    setIsInstalled(standalone);
    setIsIos(devQuery.pwaPreview() ? false : isIosDevice());
    setIsAndroid(devQuery.pwaPreview() ? true : isAndroidDevice());
    setSnoozed(devQuery.pwaPreview() ? false : isSnoozed(SNOOZE_KEY, SNOOZE_DAYS));
    setAndroidRepairSnoozed(
      devQuery.pwaPreview() ? false : isSnoozed(ANDROID_REPAIR_SNOOZE_KEY, ANDROID_REPAIR_SNOOZE_DAYS),
    );
    setDismissCount(readSnooze(SNOOZE_KEY)?.count ?? 0);

    const onBeforeInstall = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    const onAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      setRelatedWebAppInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);

    let cancelled = false;
    void probeRelatedWebAppInstalled().then((installed) => {
      if (!cancelled) setRelatedWebAppInstalled(installed);
    });

    return () => {
      cancelled = true;
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      trackEvent(AnalyticsEvents.PWA_INSTALL);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    const count = writeSnooze(SNOOZE_KEY);
    setDismissCount(count);
    setSnoozed(true);
  }, []);

  const dismissAndroidRepair = useCallback(() => {
    writeSnooze(ANDROID_REPAIR_SNOOZE_KEY);
    setAndroidRepairSnoozed(true);
  }, []);

  const needsAndroidRepair = shouldOfferAndroidPwaRepair({
    isAndroid,
    isStandalone: isInstalled,
    relatedWebAppInstalled:
      relatedWebAppInstalled || (devQuery.pwaPreview() && hasDevRepairFlag()),
  });

  return {
    canInstall: (!!deferredPrompt || devQuery.pwaPreview()) && !isInstalled,
    isInstalled,
    isIos: isIos && !isInstalled,
    isAndroid,
    needsAndroidRepair,
    isSnoozed: snoozed,
    isAndroidRepairSnoozed: androidRepairSnoozed,
    dismissCount,
    install,
    dismiss,
    dismissAndroidRepair,
  };
}

function hasDevRepairFlag(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('pwaRepair');
}
